import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { INimbusFormGroup, NimbusFormGroupBuilder, NimbusFormGroupWrapper } from '@nimbus-library/features/shared/angular/forms';

interface InputDateTimePickerFormValue {
  date?: Date;
  time?: Date;
}

@UntilDestroy()
@Component({
  selector: 'nimbus-input-date-time-picker',
  standalone: false,
  templateUrl: './input-date-time-picker.component.html',
  styleUrl: './input-date-time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => InputDateTimePickerComponent) },
    { provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => InputDateTimePickerComponent) },
  ],
})
export class InputDateTimePickerComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() public id: string;
  @Input() public label: string;
  @Input() public required = false;

  public readonly TranslationKeys = TranslationKeys;
  public readonly innerWrapper: NimbusFormGroupWrapper<InputDateTimePickerFormValue>;

  public get inner(): INimbusFormGroup<InputDateTimePickerFormValue> {
    return this.innerWrapper.form;
  }

  public constructor(
    private readonly cdr: ChangeDetectorRef,
    formGroupBuilder: NimbusFormGroupBuilder,
  ) {
    this.innerWrapper = formGroupBuilder.group<InputDateTimePickerFormValue>({
      date: [null],
      time: [null],
    });
  }

  public ngOnInit(): void {
    if (this.required) {
      this.inner.controls.date.setValidators(Validators.required);
      this.inner.controls.time.setValidators(Validators.required);
    }
    this.inner.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.onChange(this.toIsoUtc());
      this.onTouched();
    });
  }

  public writeValue(value: string | null): void {
    if (!value) {
      this.inner.setValue({ date: null, time: null }, { emitEvent: false });
    } else {
      const parsed = new Date(value);
      this.inner.setValue({ date: parsed, time: parsed }, { emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.inner.disable({ emitEvent: false });
    } else {
      this.inner.enable({ emitEvent: false });
    }
  }

  public validate(): Record<string, boolean> | null {
    if (this.required && !this.toIsoUtc()) {
      return { required: true };
    }
    return null;
  }

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  private toIsoUtc(): string | null {
    const { date, time } = this.inner.value;
    if (!date || !time) {
      return null;
    }
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), time.getSeconds() ?? 0, 0);
    return combined.toISOString();
  }
}
